/*  Starfield Generator — truly random star positions on every page load.
    Generates radial-gradient CSS and injects it as a <style> tag.
    Animations stay in starfield.css (GPU-accelerated, pure transforms).

    v2 — more natural night sky:
    • Poisson-disk placement (no clumps, no grid feel)
    • Mixed color temperatures: cool white, warm white, and a few
      champagne-gold stars that match the brand
    • Wider size/brightness variance, weighted toward faint stars
      (real skies are mostly dim stars)
    • Still only 3 compositing layers — zero extra DOM, zero JS per frame */

(() => {
  const bg = document.getElementById('site-bg');
  if (!bg) return; // layout without the backdrop — do nothing

  const rand = (min, max) => Math.random() * (max - min) + min;

  /* ── Poisson-disk-ish placement: no two stars closer than `minDist` ── */
  function generatePositions(count, minDist, maxAttempts) {
    const pts = [];
    let tries = 0;
    while (pts.length < count && tries < (maxAttempts || count * 80)) {
      const x = rand(1, 99);
      const y = rand(1, 99);
      let tooClose = false;
      for (let i = 0; i < pts.length; i++) {
        const dx = x - pts[i][0];
        const dy = y - pts[i][1];
        if (dx * dx + dy * dy < minDist * minDist) { tooClose = true; break; }
      }
      if (!tooClose) pts.push([x, y]);
      tries++;
    }
    return pts;
  }

  /* Star color temperatures — mostly cool/neutral white, a few warm,
     and rare champagne-gold accents (the brand's metal). */
  const COOL = [255, 255, 255];
  const NEUTRAL = [245, 246, 250];
  const WARM = [255, 241, 214];
  const GOLD = [240, 214, 140];

  function tintFor(layer) {
    const r = Math.random();
    if (layer === 'bright') {
      if (r < 0.14) return GOLD;   // a couple of gold jewels
      if (r < 0.38) return WARM;
      return COOL;
    }
    if (r < 0.06) return GOLD;
    if (r < 0.28) return WARM;
    if (r < 0.6) return NEUTRAL;
    return COOL;
  }

  /* ── Build a radial-gradient string from star array ── */
  function buildGradient(stars) {
    return stars.map(s =>
      `radial-gradient(${s.size}px ${s.size}px at ${s.x.toFixed(2)}% ${s.y.toFixed(2)}%, rgba(${s.tint[0]},${s.tint[1]},${s.tint[2]},${s.alpha.toFixed(4)}) ${s.inner}%, transparent ${s.outer}%)`
    ).join(',\n    ');
  }

  /* Faint-weighted brightness: most stars dim, few bright (like a real sky) */
  const faintWeighted = (lo, hi) => lo + (hi - lo) * Math.pow(Math.random(), 1.8);

  /* ── Layer configs (prime-number tiles so loops never sync) ── */
  // Base: many dim small stars
  const baseTile = 137;
  const baseStars = generatePositions(26, 10).map(([x, y]) => ({
    x, y,
    size: rand(1.0, 1.5),
    alpha: faintWeighted(0.16, 0.55),
    tint: tintFor('base'),
    inner: 50, outer: 55
  }));

  // Medium: fewer, brighter stars
  const medTile = 283;
  const medStars = generatePositions(13, 16).map(([x, y]) => ({
    x, y,
    size: rand(1.2, 1.8),
    alpha: faintWeighted(0.5, 1.0),
    tint: tintFor('med'),
    inner: 50, outer: 60
  }));

  // Bright: sparse, large twinklers — one oversized "hero" star per tile
  const brightTile = 619;
  const brightStars = generatePositions(8, 18).map(([x, y], i) => ({
    x, y,
    size: i === 0 ? rand(2.6, 3.2) : rand(1.4, 2.6),
    alpha: rand(0.88, 1.0),
    tint: tintFor('bright'),
    inner: i === 0 ? 40 : 45,
    outer: 60
  }));

  /* ── Inject <style> ── */
  const style = document.createElement('style');
  style.id = 'starfield-generated';
  style.textContent = `
#site-bg {
  background-image:
    ${buildGradient(baseStars)} !important;
  background-size: ${baseTile}px ${baseTile}px !important;
}
#site-bg::before {
  background-image:
    ${buildGradient(medStars)} !important;
  background-size: ${medTile}px ${medTile}px !important;
}
#site-bg::after {
  background-image:
    ${buildGradient(brightStars)} !important;
  background-size: ${brightTile}px ${brightTile}px !important;
}
`;
  document.head.appendChild(style);
})();
